import { costMoves } from "@ewn/shared";
import { readSupplierCosts, type AdminSupplierCostRow } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/feedback";
import { TableShell, TBody, TD, TH, THead, TR } from "@/components/ui/table";

/**
 * Each package's stored cost beside what eSIM Go charges for it now.
 *
 * Read when opened, and again after an update, so what it shows is what the
 * worker would write. The change is the worker's figure, and the rows
 * offered are picked by `costMoves`, the rule the worker writes by.
 *
 * The update sends ids only: the worker asks the supplier itself and writes
 * what it states, so a price that moved since this loaded is written as it
 * is now, not as shown here.
 *
 * A rise is in the Margin column's red, because it is the direction that
 * loses money on every sale until the price follows.
 */
export function SupplierCosts({
  busy,
  onApply,
  onClose,
}: {
  busy: boolean;
  /** Writes the costs of these packages; true once it went through. */
  onApply: (ids: string[]) => Promise<boolean>;
  onClose: () => void;
}) {
  const { data, loading, error, reload } = useAsync(() => readSupplierCosts(), []);
  const rows = data?.rows ?? [];
  const moved = rows.filter(costMoves).map((row) => row.id);

  async function apply() {
    if (await onApply(moved)) reload();
  }

  return (
    <div
      role="region"
      aria-label="eSIM Go costs"
      className="space-y-3 rounded-lg border border-border bg-muted/40 px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium">eSIM Go costs</span>
        <span className="text-xs text-muted-foreground">
          What eSIM Go charges for each bundle now. Only orders placed after an update use it.
        </span>
      </div>

      {error && <Notice tone="error">{error}</Notice>}
      {loading && !data && <p className="text-sm text-muted-foreground">Asking eSIM Go…</p>}

      {data && (
        <TableShell>
          <THead>
            <TH>Package</TH>
            <TH numeric>Cost now</TH>
            <TH numeric>eSIM Go</TH>
            <TH numeric>Change</TH>
          </THead>
          <TBody>
            {rows.map((row) => (
              <CostRow key={row.id} row={row} />
            ))}
          </TBody>
        </TableShell>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" disabled={busy || loading || moved.length === 0} onClick={apply}>
          Update {moved.length} {moved.length === 1 ? "cost" : "costs"}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

function CostRow({ row }: { row: AdminSupplierCostRow }) {
  const change = row.changeCents;
  return (
    <TR>
      <TD>
        <div className="font-medium">{row.title}</div>
        <div className="font-mono text-xs text-muted-foreground">{row.esimgoName}</div>
      </TD>
      <TD numeric className="text-secondary">{money(row.costCents)}</TD>
      <TD numeric>{money(row.supplierCents)}</TD>
      {change === null ? (
        <TD numeric className="text-xs text-muted-foreground">
          Unknown: {row.reason}
        </TD>
      ) : (
        <TD
          numeric
          className={
            change > 0
              ? "font-medium text-destructive-ink"
              : change < 0
                ? "text-success-ink"
                : "text-muted-foreground"
          }
        >
          {change === 0 ? "—" : `${change > 0 ? "+" : ""}${money(change)}`}
        </TD>
      )}
    </TR>
  );
}
